import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  useEarlyPredictionsQuery,
  useHistoricalResultsQuery,
  useLeaderboardQuery,
} from "@/api/vercel";
import { useEarlyMailForRace } from "@/hooks/useEarlyMailForRace";
import { getStoredProductName } from "@/utils/product";
import { PRODUCT_LEXI_SOCIAL } from "@/utils/vars";
import "@/styles/_global.scss";
import "@/styles/tip-highlight.css";
import { format, isPast, parseISO } from "date-fns";
import "react-circular-progressbar/dist/styles.css";
import { TIP_LINGO } from "@/utils/constants";
import { logger } from "@/utils/logger";
import { hasRaceResults } from "@/utils/tally-results";
import { raceDayTipsTime } from "@/utils/time-format";
import RenderTips from "@/utils/render-tips";
import { transformRaceData } from "@/utils/transform-race-data";
import { Clock, Search } from "lucide-react";
// import { TooltipProvider } from "@/components/ui/tooltip";
import LexiChat from "@/components/LexiChat";
import RaceNotFound from "@/components/RaceNotFound";
import BottomNavbar from "@/components/bottom-navbar";
import EarlyMailRaceSection from "@/components/EarlyMailRaceSection";
import TipMovementKey from "@/components/TipMovementKey";
import { SHOW_TIP_MARKER_KEY } from "@/utils/tip-movement";
import { useCarousel } from "@/components/layouts/RacingLayout";
import TrackRaceSelector from "@/components/track-race-selector";
import Dial from "@/components/ui/dial";
import { Spinner } from "@/components/ui/spinner";

// Utility function to format race analysis text
const formatRaceAnalysis = (text) => {
  if (!text) return "";

  let formattedText = text;

  // Handle bold text with **
  formattedText = formattedText.replace(
    /\*\*([^*]+)\*\*/g,
    '<span class="font-bold">$1</span>',
  );

  // Handle italic text with _
  formattedText = formattedText.replace(
    /_([^_]+)_/g,
    '<span class="italic">$1</span>',
  );

  return formattedText;
};

// Add a cache map to store already fetched prediction data
const predictionCache = new Map();

export default function Leaderboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const today = format(new Date(), "dd-MM-yyyy");
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [races, setRaces] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState("");
  const [selectedRace, setSelectedRace] = useState("");
  const [viewMode, setViewMode] = useState(
    searchParams.get("viewMode") || "list",
  );
  const [dialValue, setDialValue] = useState(0);
  const [trackMap, setTrackMap] = useState(new Map());
  const [isMounted, setIsMounted] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const [isTrackSelectOpen, setIsTrackSelectOpen] = useState(false);
  const [hasHistoricalData, setHasHistoricalData] = useState(false);
  const [sortedRaces, setSortedRaces] = useState([]);
  const [isTipHistoryExpanded, setIsTipHistoryExpanded] = useState(false);
  // Early Mail starts collapsed and, like the tip history, stays open across races once expanded
  const [isEarlyMailOpen, setIsEarlyMailOpen] = useState(false);
  // RenderTips reports whether movement markers are shown (for the marker key)
  const [isTipMovementShown, setIsTipMovementShown] = useState(false);
  const [isChatFocused, setIsChatFocused] = useState(false);
  const [isNextToJumpActive, setIsNextToJumpActive] = useState(false);
  const [productName, setProductName] = useState(() => getStoredProductName() || "");
  const isLexiSocial =
    (productName || "").toLowerCase() === PRODUCT_LEXI_SOCIAL.toLowerCase();

  // Use the shared carousel context
  const {
    ref: carouselRef,
    isRaceNotFound,
    nextToJump: handleNextToJump,
  } = useCarousel();

  // Add a flag to prevent unnecessary re-renders during navigation
  const isNavigating = useRef(false);

  // Add a ref to track previous leaderboard data to prevent infinite renders
  const prevDataRef = useRef(null);

  // Cache key for current query
  const currentQueryKey = useMemo(
    () => `predictions_${today}_${selectedTrack}_${selectedRace}`,
    [today, selectedTrack, selectedRace],
  );

  // Modify leaderboard query to use cached data and prevent refetching
  const {
    data: { data: leaderboardQueryData } = {},
    isError: isLeaderboardError,
    isLoading: isLeaderboardLoading,
  } = useLeaderboardQuery({
    date: today,
    collection: "predictions",
    // Add options to prevent refetching during navigation
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    cacheTime: 30 * 60 * 1000, // Keep unused data in cache for 30 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Don't refetch when component mounts if data exists
  });

  // Similarly modify other queries
  const {
    data: histResults = { data: {} },
    isError: isHistoricalError,
    isLoading: isHistoricalLoading,
  } = useHistoricalResultsQuery({
    date: today,
    fetchAll: false,
    staleTime: 10 * 60 * 1000, // Historical data changes less frequently
    cacheTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Fetch early predictions with automatic 1-minute refetch interval
  const {
    data: earlyPredictions = { data: {} },
    isLoading: isEarlyPredictionsLoading,
  } = useEarlyPredictionsQuery({
    date: today,
    staleTime: 10 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    selectedTrack,
    selectedRace,
  });

  const raceAnalysisText = leaderboardData[0]?.analysis;

  // Early Mail for the selected race, under the race tips as on /results.
  // Lexi Social only sees it once the race has results.
  const showEarlyMail =
    Boolean(selectedTrack && selectedRace) &&
    (!isLexiSocial ||
      hasRaceResults(selectedTrack, selectedRace, today, histResults.data));
  const earlyMail = useEarlyMailForRace({
    date: today,
    trackName: selectedTrack,
    raceNumber: selectedRace,
    viewedDate: new Date(),
    enabled: showEarlyMail,
  });
  // Tip movement markers: Early Mail feeds the race tips' markers, and the race tips
  // (Race Day + Late Mail) feed the Early Mail markers (src/utils/tip-movement.js).
  const selectedRaceKey =
    selectedTrack && selectedRace && today
      ? `${selectedTrack.replace(/\s+/g, "-")}-${selectedRace}-${today}`
      : null;
  const raceTipMovementSources = useMemo(
    () => ({ earlyMail: showEarlyMail ? earlyMail.earlyMailTips : null }),
    [showEarlyMail, earlyMail.earlyMailTips],
  );

  const marketStartTimeAndTips = useMemo(() => {
    if (!leaderboardQueryData || !selectedTrack || !selectedRace) {
      return { marketStartTime: null, hasTips: false };
    }

    const raceKey = `${selectedTrack.replace(/\s+/g, "-")}-${selectedRace}-${today}`;
    const raceData = leaderboardQueryData[raceKey];

    const hasTips = !!(
      raceData?.firstFourData &&
      Array.isArray(raceData.firstFourData) &&
      raceData.firstFourData.length > 0
    );
    const marketStartTime = raceData?.marketStartTime || null;

    return { marketStartTime, hasTips };
  }, [leaderboardQueryData, selectedTrack, selectedRace, today]);

  // Update view mode when URL changes
  useEffect(() => {
    const newViewMode = searchParams.get("viewMode") || "list";
    setViewMode(newViewMode);
  }, [location.search]);

  useEffect(() => {
    const handleProductChange = () => {
      try {
        setProductName(getStoredProductName() || "");
      } catch {
        setProductName("");
      }
    };

    window.addEventListener("lexi:product-updated", handleProductChange);
    window.addEventListener("storage", handleProductChange);
    handleProductChange();

    return () => {
      window.removeEventListener("lexi:product-updated", handleProductChange);
      window.removeEventListener("storage", handleProductChange);
    };
  }, []);

  // Create optimized version of handleTrackChange
  const handleTrackChange = useCallback(
    (value) => {
      // Set navigating flag to prevent unnecessary re-renders
      isNavigating.current = true;

      // Don't set isChanging/isMounted for simple navigation, just update state directly
      setSelectedTrack(value);

      let newRace = "";
      if (trackMap.has(value)) {
        const trackRaces = Array.from(trackMap.get(value)).sort(
          (a, b) => a - b,
        );
        setRaces(trackRaces);
        newRace = trackRaces[0]?.toString() || "";
        setSelectedRace(newRace);
        setDialValue(trackMap.get(value).size);
      }

      // Update URL without a reload
      const formattedTrack = value.replace(/\s+/g, "-").toLowerCase();
      const url = new URL(window.location.href);
      url.searchParams.set("track", formattedTrack);
      url.searchParams.set("race", newRace);
      window.history.pushState({}, "", url);

      // Dispatch the event for other components
      window.dispatchEvent(
        new CustomEvent("urlchange", {
          detail: { track: formattedTrack, race: newRace },
        }),
      );

      // Notify about navigation state change
      window.dispatchEvent(new CustomEvent("navigationStateChange"));

      setIsTrackSelectOpen(false);

      // Reset the navigating flag
      setTimeout(() => {
        isNavigating.current = false;
        // Notify again after navigation is done
        window.dispatchEvent(new CustomEvent("navigationStateChange"));
      }, 50);
    },
    [trackMap],
  );

  // Similarly optimize handleRaceChange
  const handleRaceChange = useCallback(
    (value) => {
      // Set navigating flag to prevent unnecessary re-renders
      isNavigating.current = true;

      setSelectedRace(value);

      if (selectedTrack) {
        const formattedTrack = selectedTrack.replace(/\s+/g, "-").toLowerCase();
        const url = new URL(window.location.href);
        url.searchParams.set("track", formattedTrack);
        url.searchParams.set("race", value);
        window.history.pushState({}, "", url);

        window.dispatchEvent(
          new CustomEvent("urlchange", {
            detail: { track: formattedTrack, race: value },
          }),
        );

        // Notify about navigation state change
        window.dispatchEvent(new CustomEvent("navigationStateChange"));
      }

      // Reset the navigating flag
      setTimeout(() => {
        isNavigating.current = false;
        // Notify again after navigation is done
        window.dispatchEvent(new CustomEvent("navigationStateChange"));
      }, 50);
    },
    [selectedTrack],
  );

  const handleDialChange = (newValue) => {
    handleRaceChange(newValue);
  };

  // Add the toggleTipHistory function
  const toggleTipHistory = () => {
    setIsTipHistoryExpanded(!isTipHistoryExpanded);
  };

  // Also optimize handleRaceNavigation
  const handleRaceNavigation = useCallback(
    (newTrack, newRace) => {
      // When a navigation is in progress, set the flag
      isNavigating.current = true;

      // Notify about navigation state change
      window.dispatchEvent(new CustomEvent("navigationStateChange"));

      // Update the URL without reloading
      const formattedTrack = newTrack.replace(/\s+/g, "-").toLowerCase();
      const url = new URL(window.location.href);
      url.searchParams.set("track", formattedTrack);
      url.searchParams.set("race", newRace);
      window.history.pushState({}, "", url);

      // Notify other components
      window.dispatchEvent(
        new CustomEvent("urlchange", {
          detail: { track: formattedTrack, race: newRace },
        }),
      );

      // Update local state directly without the transitional states
      if (trackMap.has(newTrack)) {
        const trackRaces = Array.from(trackMap.get(newTrack)).sort(
          (a, b) => a - b,
        );
        setRaces(trackRaces);
        setSelectedTrack(newTrack);
        setSelectedRace(newRace);
        setDialValue(trackMap.get(newTrack).size);
      }

      // Reset the navigating flag
      setTimeout(() => {
        isNavigating.current = false;
        // Notify again after navigation is done
        window.dispatchEvent(new CustomEvent("navigationStateChange"));
      }, 50);
    },
    [trackMap],
  );

  // Add a listener for URL changes that doesn't reset the mount state
  useEffect(() => {
    const handleUrlChange = (event) => {
      const { track, race } = event.detail;

      // If navigation is already in progress, skip this update
      if (isNavigating.current) return;

      // Otherwise, set the navigating flag
      isNavigating.current = true;

      // Dispatch navigation state change event
      window.dispatchEvent(new CustomEvent("navigationStateChange"));

      // Update the component state directly without unmounting
      if (track && race) {
        // Find the exact track match (handle hyphenated track names)
        const matchedTrack = tracks.find(
          (t) => t.replace(/\s+/g, "-").toLowerCase() === track.toLowerCase(),
        );

        if (matchedTrack && trackMap.has(matchedTrack)) {
          setSelectedTrack(matchedTrack);
          setSelectedRace(race);

          const trackRaces = Array.from(trackMap.get(matchedTrack)).sort(
            (a, b) => a - b,
          );
          setRaces(trackRaces);
          setDialValue(trackRaces.length);
        }
      }

      // Reset the navigating flag
      setTimeout(() => {
        isNavigating.current = false;
        // Dispatch event when done navigating
        window.dispatchEvent(new CustomEvent("navigationStateChange"));
      }, 50);
    };

    window.addEventListener("urlchange", handleUrlChange);

    // Also handle browser back/forward navigation
    const handlePopState = () => {
      const searchParams = new URLSearchParams(window.location.search);
      const trackParam = searchParams.get("track");
      const raceParam = searchParams.get("race");

      if (trackParam && raceParam && !isNavigating.current) {
        // Manually dispatch urlchange event to reuse the same handler
        window.dispatchEvent(
          new CustomEvent("urlchange", {
            detail: { track: trackParam, race: raceParam },
          }),
        );
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("urlchange", handleUrlChange);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [tracks, trackMap]); // Only depend on tracks and trackMap, not on mutable state

  // Use this effect to prevent the white flash during navigation
  useEffect(() => {
    // Only show loading state for initial data load, not during navigation
    if (isNavigating.current) {
      setIsChanging(false);
      setIsMounted(true);
    }
  }, []); // Remove isNavigating.current from dependencies, it's a mutable ref

  // Add separate event handler for URL changes that updates UI state
  useEffect(() => {
    const handleNavigationStateChange = () => {
      if (isNavigating.current) {
        setIsChanging(false);
        setIsMounted(true);
      }
    };

    // Run on mount and when navigation state changes
    window.addEventListener(
      "navigationStateChange",
      handleNavigationStateChange,
    );

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener(
        "navigationStateChange",
        handleNavigationStateChange,
      );
    };
  }, []);

  useEffect(() => {
    // Initial setup when data is first loaded
    if (!leaderboardQueryData) {
      return;
    }

    const newTrackMap = new Map();
    Object.entries(leaderboardQueryData).forEach(([key, raceData]) => {
      if (!raceData || !raceData.track || !raceData.race) {
        return;
      }
      if (!newTrackMap.has(raceData.track)) {
        newTrackMap.set(raceData.track, new Set());
      }
      newTrackMap.get(raceData.track).add(raceData.race);
    });

    setTrackMap(newTrackMap);
    const tracksArray = Array.from(newTrackMap.keys()).sort();
    setTracks(tracksArray);

    // Check for track and race query parameters
    const trackParam = searchParams.get("track");
    const raceParam = searchParams.get("race");

    if (trackParam && raceParam) {
      // Convert URL parameter (kebab-case) back to original track name
      const matchingTrack = tracksArray.find(
        (track) =>
          track.replace(/\s+/g, "-").toLowerCase() === trackParam.toLowerCase(),
      );

      if (matchingTrack && newTrackMap.has(matchingTrack)) {
        // We found a matching track
        setSelectedTrack(matchingTrack);

        // Check if the race exists for this track
        const trackRaces = Array.from(newTrackMap.get(matchingTrack)).sort(
          (a, b) => a - b,
        );
        setRaces(trackRaces);

        if (trackRaces.includes(parseInt(raceParam))) {
          setSelectedRace(raceParam);
          setDialValue(trackRaces.length);
        } else {
          // Race doesn't exist for this track - select the first race instead
          if (trackRaces.length > 0) {
            const firstRace = trackRaces[0].toString();
            setSelectedRace(firstRace);
            setDialValue(trackRaces.length);

            // Update URL with first race
            const formattedTrack = matchingTrack
              .replace(/\s+/g, "-")
              .toLowerCase();
            const url = new URL(window.location.href);
            url.searchParams.set("track", formattedTrack);
            url.searchParams.set("race", firstRace);
            window.history.pushState({}, "", url);
          }
        }

        // Exit here and don't run any of the default selection logic below
        return;
      } else {
        // Track doesn't exist - redirect to first track and race
        if (tracksArray.length > 0) {
          const firstTrack = tracksArray[0];
          setSelectedTrack(firstTrack);

          if (newTrackMap.has(firstTrack)) {
            const trackRaces = Array.from(newTrackMap.get(firstTrack)).sort(
              (a, b) => a - b,
            );
            setRaces(trackRaces);

            if (trackRaces.length > 0) {
              const firstRace = trackRaces[0].toString();
              setSelectedRace(firstRace);
              setDialValue(trackRaces.length);

              // Update URL with first track and race
              const formattedTrack = firstTrack
                .replace(/\s+/g, "-")
                .toLowerCase();
              const url = new URL(window.location.href);
              url.searchParams.set("track", formattedTrack);
              url.searchParams.set("race", firstRace);
              window.history.pushState({}, "", url);
            }
          }
        }
        return;
      }
    }

    // ONLY run default selection logic if there are NO URL parameters
    if (!trackParam && !raceParam) {
      // Find the next race to jump
      const now = new Date();
      const allRaces = Object.entries(leaderboardQueryData)
        .map(([key, race]) => ({
          track: race.track,
          race: race.race,
          jumpTime: race.marketStartTime
            ? new Date(race.marketStartTime)
            : null,
          key,
        }))
        .filter((race) => race.jumpTime !== null)
        .sort((a, b) => a.jumpTime.getTime() - b.jumpTime.getTime());

      // Find the next race to jump (first race that hasn't started yet)
      const nextRace = allRaces.find(
        (race) => race.jumpTime.getTime() > now.getTime(),
      );

      if (nextRace && nextRace.track) {
        // Set the selected track and race to the next race to jump
        setSelectedTrack(nextRace.track);

        // Update URL with the next race
        const formattedTrack = nextRace.track
          .replace(/\s+/g, "-")
          .toLowerCase();
        const url = new URL(window.location.href);
        url.searchParams.set("track", formattedTrack);
        url.searchParams.set("race", nextRace.race.toString());
        window.history.pushState({}, "", url);

        // Notify components of the URL change
        window.dispatchEvent(
          new CustomEvent("urlchange", {
            detail: { track: formattedTrack, race: nextRace.race.toString() },
          }),
        );

        // Set races and dial value
        if (newTrackMap.has(nextRace.track)) {
          const trackRaces = Array.from(newTrackMap.get(nextRace.track)).sort(
            (a, b) => a - b,
          );
          setRaces(trackRaces);
          setSelectedRace(nextRace.race.toString());
          setDialValue(trackRaces.length);
        }
      } else if (!selectedTrack && tracksArray.length > 0) {
        // If no next race found, default to first race (old behavior)
        const initialTrack = tracksArray[0];
        setSelectedTrack(initialTrack);

        if (initialTrack && newTrackMap.has(initialTrack)) {
          const trackRaces = Array.from(newTrackMap.get(initialTrack)).sort(
            (a, b) => a - b,
          );
          setRaces(trackRaces);
          if (trackRaces.length > 0) {
            setSelectedRace(trackRaces[0].toString());
            setDialValue(trackRaces.length);
          }
        }
      }
    }
  }, [leaderboardQueryData]); // Only run this effect when leaderboardQueryData changes

  // This useEffect should also respect URL parameters and not override them
  useEffect(() => {
    // Process the leaderboard data to extract race information
    if (leaderboardQueryData && Object.keys(leaderboardQueryData).length > 0) {
      try {
        // Process the data and update state
        const processedData = Object.entries(leaderboardQueryData)
          .map(([key, race]) => {
            if (!race || !race.marketStartTime) return null;
            return {
              id: key,
              track: race.track,
              raceNumber: race.race,
              marketStartTime: parseISO(race.marketStartTime),
              isCompleted: isPast(parseISO(race.marketStartTime)),
              // ... rest of the processing
            };
          })
          .filter((race) => race !== null);

        setLeaderboardData(processedData);
        setIsMounted(true);
      } catch (error) {
        logger.error("Error processing leaderboard data:", error);
      }
    }
  }, [leaderboardQueryData, selectedTrack, selectedRace]);

  // Add a new effect to handle initial data loading
  useEffect(() => {
    if (!leaderboardQueryData || !selectedTrack || !selectedRace) {
      return;
    }

    const raceKey = `${selectedTrack.replace(/\s+/g, "-")}-${selectedRace}-${today}`;
    const raceData = leaderboardQueryData[raceKey];

    if (raceData?.firstFourData && Array.isArray(raceData.firstFourData)) {
      const transformedData = transformRaceData(
        raceData,
        { includeConfidence: true },
        earlyPredictions.data,
      );

      // Use the prevDataRef to avoid dependency on leaderboardData state
      // Store the JSON string of transformedData for comparison
      const transformedDataString = JSON.stringify(transformedData);
      const prevDataString = prevDataRef.current;

      // Only update if data has changed
      if (transformedDataString !== prevDataString) {
        prevDataRef.current = transformedDataString;
        setIsMounted(false);
        setLeaderboardData(transformedData);
        setTimeout(() => setIsMounted(true), 50);
      }
    } else {
      // Only set empty data if we haven't already
      if (prevDataRef.current !== "[]") {
        prevDataRef.current = "[]";
        setLeaderboardData([]);
      }
    }
  }, [
    leaderboardQueryData,
    selectedTrack,
    selectedRace,
    earlyPredictions.data,
    // leaderboardData removed from dependencies to prevent infinite loop
  ]);

  useEffect(() => {
    if (!histResults.data || isNavigating.current) return;

    const currentRaceKey = `${selectedTrack.replace(/\s+/g, "-")}-${selectedRace}-${today}`;
    const hasHistoricalDataForCurrentRace = Object.keys(
      histResults.data,
    ).includes(currentRaceKey);

    // logger.log("hasHistoricalDataForCurrentRace: ", hasHistoricalDataForCurrentRace);

    // Only update if the value has changed
    if (hasHistoricalDataForCurrentRace !== hasHistoricalData) {
      setHasHistoricalData(hasHistoricalDataForCurrentRace);
    }
  }, [histResults.data, selectedTrack, selectedRace, hasHistoricalData]);

  useEffect(() => {
    // Skip if we're navigating or if required data is missing
    if (isNavigating.current || !leaderboardQueryData || trackMap.size === 0) {
      return;
    }

    // Check if we came from the "Next to Jump" button
    const params = new URLSearchParams(window.location.search);
    const shouldJump = params.get("nextToJump") === "true";

    if (shouldJump) {
      // Remove "nextToJump" parameter from URL to avoid duplicate handling
      const url = new URL(window.location.href);
      url.searchParams.delete("nextToJump");
      window.history.pushState({}, "", url);

      // Find the next race
      const now = new Date();
      const allRaces = Object.entries(leaderboardQueryData)
        .map(([key, race]) => ({
          track: race.track,
          race: race.race,
          jumpTime: race.marketStartTime
            ? new Date(race.marketStartTime)
            : null,
          key,
        }))
        .filter((race) => race.jumpTime !== null)
        .sort((a, b) => a.jumpTime.getTime() - b.jumpTime.getTime());

      const nextRace = allRaces.find(
        (race) => race.jumpTime.getTime() > now.getTime(),
      );

      if (nextRace && trackMap.has(nextRace.track)) {
        const trackRaces = Array.from(trackMap.get(nextRace.track)).sort(
          (a, b) => a - b,
        );
        setRaces(trackRaces);
        setDialValue(trackRaces.length);

        // Update URL with the next race parameters instead of nextToJump=true
        const formattedTrack = nextRace.track
          .replace(/\s+/g, "-")
          .toLowerCase();
        navigate(`?track=${formattedTrack}&race=${nextRace.race}`, {
          replace: true,
        });

        // Update component state
        setSelectedTrack(nextRace.track);
        setTimeout(() => {
          setSelectedRace(nextRace.race.toString());
        }, 100);
      }
    }
  }, [leaderboardQueryData, trackMap, navigate]);

  // TODO: auto refresh early predictions
  const hasEarlyPredictions = useMemo(() => {
    if (!earlyPredictions.data || !selectedTrack || !selectedRace) return false;

    const raceKey = `${selectedTrack.replace(/\s+/g, "-")}-${selectedRace}-${today}`;
    const firstFourData = earlyPredictions.data[raceKey]?.firstFourData;

    // Get tipUpdatedAt values for both datasets
    const regularTipUpdatedAt = leaderboardQueryData?.[raceKey]?.tipUpdatedAt;
    const earlyTipUpdatedAt = earlyPredictions.data?.[raceKey]?.tipUpdatedAt;

    // If tipUpdatedAt values are the same, consider there are no early predictions
    if (regularTipUpdatedAt === earlyTipUpdatedAt) {
      return false;
    }

    return (
      firstFourData && Array.isArray(firstFourData) && firstFourData.length > 0
    );
  }, [
    earlyPredictions.data,
    selectedTrack,
    selectedRace,
    today,
    leaderboardQueryData,
  ]);

  // Add effect to manage tip history expanded state
  useEffect(() => {
    // If there are no early predictions, ensure the tip history is collapsed
    if (!hasEarlyPredictions) {
      setIsTipHistoryExpanded(false);
    }
  }, [hasEarlyPredictions, selectedTrack, selectedRace]);

  // Add a helper function to transform early predictions into the correct format
  const transformEarlyPredictionsToLeaderboard = useCallback((earlyData, raceKey) => {
    if (!earlyData || !earlyData[raceKey]?.firstFourData) {
      return [];
    }

    const firstFourData = earlyData[raceKey].firstFourData;
    if (!Array.isArray(firstFourData) || firstFourData.length === 0) {
      return [];
    }

    return firstFourData.map((horse, index) => {
      // Parse the formatted name to get number and name
      const parts = horse.formattedName?.split('. ');
      const number = parts?.[0] || '#';
      const name = parts?.length > 1 ? parts.slice(1).join(' ').trim() : 'Unknown Horse';

      return {
        rank: index + 1,
        name: name,
        number: number,
        silks: horse.silks,
        currentPrice: horse.currentPrice,
        selectionId: horse.selectionId,
        confidence: horse.currentPrice <= 3 ? 'high' : horse.currentPrice <= 6 ? 'medium' : 'low',
        analysis: earlyData[raceKey]?.decisionExplanation || '',
      };
    });
  }, []);

  // Add logic to determine if we should use early predictions as fallback
  const { shouldUseFallback, fallbackData } = useMemo(() => {
    if (!selectedTrack || !selectedRace || !today) {
      return { shouldUseFallback: false, fallbackData: [] };
    }

    const raceKey = `${selectedTrack.replace(/\s+/g, "-")}-${selectedRace}-${today}`;
    
    // Check if predictions collection has data for this race
    const hasRegularPredictions = !!(
      leaderboardQueryData?.[raceKey]?.firstFourData &&
      Array.isArray(leaderboardQueryData[raceKey].firstFourData) &&
      leaderboardQueryData[raceKey].firstFourData.length > 0
    );

    // If we have regular predictions, don't use fallback
    if (hasRegularPredictions) {
      return { shouldUseFallback: false, fallbackData: [] };
    }

    // Check if early predictions collection has data for this race
    const hasEarlyPredictions = !!(
      earlyPredictions.data?.[raceKey]?.firstFourData &&
      Array.isArray(earlyPredictions.data[raceKey].firstFourData) &&
      earlyPredictions.data[raceKey].firstFourData.length > 0
    );

    if (hasEarlyPredictions) {
      const transformedData = transformEarlyPredictionsToLeaderboard(earlyPredictions.data, raceKey);
      return { shouldUseFallback: true, fallbackData: transformedData };
    }

    return { shouldUseFallback: false, fallbackData: [] };
  }, [leaderboardQueryData, earlyPredictions.data, selectedTrack, selectedRace, today, transformEarlyPredictionsToLeaderboard]);

  // The race tips the Early Mail markers compare with (same data RenderTips shows above it)
  const earlyMailTipMovementSources = useMemo(
    () => ({
      raceDay: selectedRaceKey ? earlyPredictions.data?.[selectedRaceKey] : null,
      latest:
        selectedRaceKey && !shouldUseFallback
          ? leaderboardQueryData?.[selectedRaceKey]
          : null,
    }),
    [selectedRaceKey, earlyPredictions.data, leaderboardQueryData, shouldUseFallback],
  );

  // Create context data for the chat
  const chatContext = useMemo(() => {
    // Ensure race is always a string
    const raceString = selectedRace?.toString() || "";

    // Get the race key
    const raceKey = `${selectedTrack?.replace(/\s+/g, "-")}-${selectedRace}-${today}`;

    // Get both analyses - prioritize fallback data if we're using it
    const currentAnalysis = shouldUseFallback 
      ? earlyPredictions.data?.[raceKey]?.decisionExplanation
      : leaderboardQueryData?.[raceKey]?.decisionExplanation;
    const earlyAnalysis = earlyPredictions.data?.[raceKey]?.decisionExplanation;

    // Use fallback data for race data if needed
    const contextRaceData = shouldUseFallback ? fallbackData : leaderboardData;

    // Debug logging for selectedTrack
    // logger.log("DEBUG - AI Tips Chat - selectedTrack:", selectedTrack);
    // logger.log("DEBUG - AI Tips Chat - selectedTrack type:", typeof selectedTrack);
    // logger.log("DEBUG - AI Tips Chat - selectedTrack is undefined:", selectedTrack === undefined);
    // logger.log("DEBUG - AI Tips Chat - selectedTrack is null:", selectedTrack === null);
    // logger.log("DEBUG - AI Tips Chat - selectedTrack is empty string:", selectedTrack === "");

    return {
      track: selectedTrack,
      race: raceString,
      date: today,
      hasHistoricalData,
      currentRaceData: contextRaceData,
      earlyAnalysis: earlyAnalysis,
      currentAnalysis: currentAnalysis,
      hasEarlyPredictions: hasEarlyPredictions,
      isUsingFallback: shouldUseFallback, // Add flag to indicate fallback usage
    };
  }, [
    selectedTrack,
    selectedRace,
    today,
    hasHistoricalData,
    leaderboardData,
    fallbackData,
    shouldUseFallback,
    earlyPredictions.data,
    leaderboardQueryData,
    hasEarlyPredictions,
  ]);

  // Add event listeners for chat focus
  useEffect(() => {
    const chatInput = document.querySelector(".lexi-chat-container input");
    if (!chatInput) return;

    const handleFocus = () => document.body.classList.add("chat-focused");
    const handleBlur = () => document.body.classList.remove("chat-focused");

    chatInput.addEventListener("focus", handleFocus);
    chatInput.addEventListener("blur", handleBlur);

    return () => {
      chatInput.removeEventListener("focus", handleFocus);
      chatInput.removeEventListener("blur", handleBlur);
      document.body.classList.remove("chat-focused"); // Cleanup on unmount
    };
  }, []); // Empty dependency array since we only want to set this up once

  // Add keyboard event listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if any element within the chat container has focus
      const chatContainer = document.querySelector(".lexi-chat-container");
      const activeElement = document.activeElement;

      // If the active element is within the chat container, stop all keyboard navigation immediately
      if (chatContainer?.contains(activeElement)) {
        // Stop the event from bubbling up and prevent any default behavior
        e.stopPropagation();
        e.stopImmediatePropagation();
        return;
      }

      // Check if any input element has focus
      const isInputFocused =
        activeElement.tagName === "INPUT" ||
        activeElement.tagName === "TEXTAREA" ||
        activeElement.isContentEditable;

      if (isInputFocused) return;

      // Only handle arrow keys if chat is not focused
      if (e.key === "ArrowRight") {
        e.preventDefault();
        e.stopPropagation();

        // Use carousel navigation if available
        if (carouselRef.current && carouselRef.current.canNavigateNext()) {
          carouselRef.current.navigateToNextRace();
        }
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        e.stopPropagation();

        // Use carousel navigation if available
        if (carouselRef.current && carouselRef.current.canNavigatePrevious()) {
          carouselRef.current.navigateToPreviousRace();
        }
      } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        // Keep track navigation for up/down arrows
        e.preventDefault();
        e.stopPropagation();
        const currentTrackIndex = tracks.indexOf(selectedTrack);
        if (e.key === "ArrowUp" && currentTrackIndex > 0) {
          handleTrackChange(tracks[currentTrackIndex - 1]);
        } else if (
          e.key === "ArrowDown" &&
          currentTrackIndex < tracks.length - 1
        ) {
          handleTrackChange(tracks[currentTrackIndex + 1]);
        }
      }
    };

    // Use capture phase to ensure our handler runs first
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [selectedTrack, tracks, carouselRef]);

  useEffect(() => {
    // Check if we're viewing the next race to jump
    if (!leaderboardQueryData || !selectedTrack || !selectedRace) {
      setIsNextToJumpActive(false);
      return;
    }

    const now = new Date();
    const allRaces = Object.entries(leaderboardQueryData)
      .map(([key, race]) => ({
        track: race.track,
        race: race.race,
        jumpTime: race.marketStartTime ? new Date(race.marketStartTime) : null,
        key,
      }))
      .filter((race) => race.jumpTime !== null)
      .sort((a, b) => a.jumpTime.getTime() - b.jumpTime.getTime());

    const nextRace = allRaces.find(
      (race) => race.jumpTime.getTime() > now.getTime(),
    );

    if (nextRace) {
      setIsNextToJumpActive(
        nextRace.track === selectedTrack &&
          nextRace.race.toString() === selectedRace,
      );
    } else {
      setIsNextToJumpActive(false);
    }
  }, [leaderboardQueryData, selectedTrack, selectedRace]);

  // Ensure proper integration with carousel next-to-jump
  const jumpToNextRace = useCallback(() => {
    if (carouselRef.current && carouselRef.current._getNextAvailableRace) {
      const nextRace = carouselRef.current._getNextAvailableRace();
      if (nextRace) {
        setSelectedTrack(nextRace.track);
        setSelectedRace(nextRace.raceNumber.toString());

        // Update URL with next race parameters
        const formattedTrack = nextRace.track
          .replace(/\s+/g, "-")
          .toLowerCase();
        const url = new URL(window.location.href);
        url.searchParams.set("track", formattedTrack);
        url.searchParams.set("race", nextRace.raceNumber.toString());
        window.history.pushState({}, "", url);

        // Dispatch the event for other components
        window.dispatchEvent(
          new CustomEvent("urlchange", {
            detail: {
              track: formattedTrack,
              race: nextRace.raceNumber.toString(),
            },
          }),
        );
      }
    }
  }, [carouselRef]);

  // Update the handler for Next To Jump button
  const handleNextToJumpClick = useCallback(() => {
    if (carouselRef.current && carouselRef.current.navigateToNextRace) {
      carouselRef.current.navigateToNextRace();
    } else {
      jumpToNextRace();
    }
  }, [carouselRef, jumpToNextRace]);

  // Replace the imported handleNextToJump with our new function
  useEffect(() => {
    if (handleNextToJump && typeof handleNextToJump === "function") {
      // We can't override the imported function directly, so just log that we're using it
      logger.log("Using carousel's handleNextToJump function for navigation");
    }
  }, [handleNextToJumpClick]);

  // Add an effect to ensure initial load uses next-to-jump when no query parameters
  useEffect(() => {
    // Skip if we have query parameters, are navigating, or don't have data yet
    const params = new URLSearchParams(window.location.search);
    const hasTrackParam = params.has("track");
    const hasRaceParam = params.has("race");

    if (
      (hasTrackParam && hasRaceParam) ||
      isNavigating.current ||
      !leaderboardQueryData ||
      Object.keys(leaderboardQueryData).length === 0
    ) {
      return;
    }

    // On initial load with no parameters, jump to next race
    // First try using the carousel reference if available
    if (carouselRef.current && carouselRef.current._getNextAvailableRace) {
      const nextRace = carouselRef.current._getNextAvailableRace();
      if (nextRace) {
        jumpToNextRace();
        return;
      }
    }

    // Fallback: Find the next race to jump directly from leaderboard data
    const now = new Date();
    const allRaces = Object.entries(leaderboardQueryData)
      .map(([key, race]) => ({
        track: race.track,
        race: race.race,
        jumpTime: race.marketStartTime ? new Date(race.marketStartTime) : null,
        key,
      }))
      .filter((race) => race.jumpTime !== null)
      .sort((a, b) => a.jumpTime.getTime() - b.jumpTime.getTime());

    const nextRace = allRaces.find(
      (race) => race.jumpTime.getTime() > now.getTime(),
    );

    if (nextRace && trackMap.has(nextRace.track)) {
      // Set up races for the track
      const trackRaces = Array.from(trackMap.get(nextRace.track)).sort(
        (a, b) => a - b,
      );
      setRaces(trackRaces);
      setDialValue(trackRaces.length);

      // Update URL and state
      const formattedTrack = nextRace.track.replace(/\s+/g, "-").toLowerCase();
      navigate(`?track=${formattedTrack}&race=${nextRace.race}`, {
        replace: true,
      });

      // Update component state
      setSelectedTrack(nextRace.track);
      setSelectedRace(nextRace.race.toString());
    }
  }, [leaderboardQueryData, trackMap, carouselRef, jumpToNextRace, navigate]);

  // Only show loading spinner if we're actually loading and have no data
  if (isLeaderboardLoading && !leaderboardQueryData) {
    return (
      <div className="flex justify-center items-center py-16">
        <Spinner className="w-8 h-8" />
      </div>
    );
  }

  // Show error state if there's an error
  if (isLeaderboardError) {
    return (
      <div className="text-center py-8 px-4">
        <p className="text-red-500 mb-2">Error loading tips</p>
        <p className="text-gray-600">Please try again later.</p>
      </div>
    );
  }

  const renderContent = () => {
    // Get a random tip from TIP_LINGO.favourite with null check
    const favouriteTips = TIP_LINGO.favourite || [];
    const randomTip =
      favouriteTips.length > 0
        ? favouriteTips[Math.floor(Math.random() * favouriteTips.length)]
        : "Welcome to Lexi's Tips!";

    const raceAnalysis = raceAnalysisText ? (
      <div className="race-analysis-container flex justify-center items-center">
        <div className="race-analysis-inner">
          <p className="text-md mb-2 font-bold">Race Analysis</p>
          <p
            className="text-xs text-black"
            dangerouslySetInnerHTML={{
              __html: formatRaceAnalysis(raceAnalysisText),
            }}
          />
        </div>
      </div>
    ) : null;

    if (isRaceNotFound) {
      return <RaceNotFound onNextToJump={handleNextToJumpClick} />;
    }

    if (viewMode === "list") {
      return (
        <div className="tips-master-container">
          {/* Updated content with fallback logic */}
          {leaderboardData.length === 0 && !shouldUseFallback ? (
            <div className="flex flex-col items-center justify-center py-6 text-gray-500">
              {/* Check if there are any tips in the entire system */}
              {!leaderboardQueryData ||
              Object.values(leaderboardQueryData).every(
                (race) =>
                  !race.firstFourData || race.firstFourData.length === 0,
              ) ? (
                <>
                  <Clock className="w-12 h-12 mb-4 text-[#f0a7d6]" />
                  <h3 className="text-sm text-center mb-4">
                    <span className="font-bold">Tips being generated.</span>
                    <br />
                    <span className="">Try again at {raceDayTipsTime()}</span>
                  </h3>
                  <p className="text-sm text-center mb-4 max-w-[250px]">
                    In the meantime, why not review{" "}
                    <Link
                      to="/results"
                      className="text-sm text-[#f0a7d6] hover:text-[#d580b0] underline"
                    >
                      yesterdays results
                    </Link>{" "}
                    while you wait?
                  </p>
                </>
              ) : (
                <>
                  <Search className="w-12 h-12 mb-4 text-gray-400" />
                  <p className="text-sm font-bold">No tips available</p>
                </>
              )}
            </div>
          ) : (
            <RenderTips
              selectedTrack={selectedTrack}
              selectedRace={selectedRace}
              today={today}
              isLexiSocial={isLexiSocial}
              isChanging={isChanging}
              isMounted={isMounted}
              isTipHistoryExpanded={isTipHistoryExpanded}
              toggleTipHistory={toggleTipHistory}
              // Pass fallback data if needed
              externalLeaderboardData={shouldUseFallback ? fallbackData : null}
              externalLeaderboardQueryData={shouldUseFallback ? earlyPredictions.data : null}
              externalEarlyPredictionsData={shouldUseFallback ? earlyPredictions.data : null}
              externalHasEarlyPredictions={shouldUseFallback ? false : null} // Don't show early predictions section when using fallback
              externalHistoricalData={histResults.data}
              externalHasHistoricalData={hasHistoricalData}
              tipMovementSources={raceTipMovementSources}
              onTipMovementShownChange={setIsTipMovementShown}
              showPriceToggle={true}
            />
          )}

          {showEarlyMail && (
            <EarlyMailRaceSection
              earlyMail={earlyMail}
              isOpen={isEarlyMailOpen}
              onToggle={() => setIsEarlyMailOpen((v) => !v)}
              trackName={selectedTrack}
              raceNumber={selectedRace}
              date={today}
              historicalData={histResults.data}
              tipMovementSources={earlyMailTipMovementSources}
              // No card of its own: lines up with Late Mail / Race Day tips
              embedded={true}
            />
          )}

          {SHOW_TIP_MARKER_KEY && isTipMovementShown && <TipMovementKey />}
        </div>
      );
    }

    // Use fallback data for dial view if needed
    const dialTipsData = shouldUseFallback ? fallbackData : leaderboardData;

    return (
      <div className="dial-container px-4 pb-4 z-10 relative overflow-hidden backdrop-blur-[1.5px]">
        <div className="tips-dial-inner pt-4 pb-4">
          <div className="tips-dial-inner-content">
            <Dial
              value={parseInt(selectedRace) || 0}
              maxValue={dialValue}
              spacing={"equalSpaced"}
              tips={dialTipsData.map((entry) => ({
                ...entry,
                displayText: (
                  <>
                    <span className="text-[#F30099] mr-1">{entry.rank}.</span>
                    <span className="text-black font-medium">
                      {entry.number}.
                    </span>
                    <span className="text-black ml-1">{entry.name}</span>
                  </>
                ),
              }))}
              onChange={handleDialChange}
            />
          </div>
        </div>
        {raceAnalysis}
      </div>
    );
  };

  // Render main UI layout
  return (
    <div className="leaderboard-container flex flex-col relative h-full bg-white">
      {!isMounted && !isNavigating.current ? (
        <div className="tips-loading flex items-center justify-center py-16">
          <Spinner size="large" className="text-[#F30099]" />
        </div>
      ) : (
        <>
          {/* SHOW THE TIPS */}
          <div className="tips-master-container-wrapper">{renderContent()}</div>

          {/* SHOW THE LEXI CHAT BLOCK */}
          {viewMode === "list" && (
            <div className="lexi-chat-container pb-[60px] flex-1 flex overflow-hidden w-full">
              <LexiChat
                contextData={{
                  ...chatContext,
                  tracks,
                  races,
                  trackMap,
                }}
                analysis={chatContext.currentAnalysis}
                earlyAnalysis={chatContext.earlyAnalysis}
                className="w-full"
                onNavigate={handleRaceNavigation}
                viewMode={viewMode}
                isLexiSocial={isLexiSocial}
                // Pass navigation handlers that use the carousel ref
                onCarouselPrevious={() =>
                  carouselRef.current?.navigateToPreviousRace()
                }
                onCarouselNext={() => carouselRef.current?.navigateToNextRace()}
                canCarouselPrevious={() =>
                  carouselRef.current?.canNavigatePrevious() || false
                }
                canCarouselNext={() =>
                  carouselRef.current?.canNavigateNext() || false
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
